"use client";

import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 border border-border bg-background/40">
        <p className="font-default text-xl text-foreground">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-border text-foreground hover:bg-primary hover:text-primary-foreground">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-8">
      <FormField control={control} name="name" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">
              Point of Contact
            </FormLabel>
            <FormControl>
              <Input {...field} placeholder="Full Name" className="h-12 px-0 bg-transparent border-0 border-b border-border font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="company" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">
              Organization
            </FormLabel>
            <FormControl>
              <Input {...field} placeholder="Company or Agency Name" className="h-12 px-0 bg-transparent border-0 border-b border-border font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <FormField control={control} name="email" render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">
                Email Address
              </FormLabel>
              <FormControl>
                <Input {...field} type="email" placeholder="name@company.com" className="h-12 px-0 bg-transparent border-0 border-b border-border font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem className="gap-3">
              <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">
                Phone Number
              </FormLabel>
              <FormControl>
                <Input {...field} type="tel" placeholder="+1 (555) 000-0000" className="h-12 px-0 bg-transparent border-0 border-b border-border font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="inquiry_type" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">
              Service Category
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="h-12 px-0 bg-transparent border-0 border-b border-border font-default text-lg text-foreground focus:border-primary focus:ring-0 data-[placeholder]:text-muted-foreground/60 w-full">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="border-border bg-popover">
                  <SelectItem value="Legend Plates" className="font-default">Legend Plates</SelectItem>
                  <SelectItem value="Stainless Steel Engraving" className="font-default">Stainless Steel Engraving</SelectItem>
                  <SelectItem value="Arc Flash Labels" className="font-default">Arc Flash Labels</SelectItem>
                  <SelectItem value="Direct Part Marking" className="font-default">Direct Part Marking</SelectItem>
                  <SelectItem value="Phenolic Labels" className="font-default">Phenolic Labels</SelectItem>
                  <SelectItem value="Acrylic Signage" className="font-default">Acrylic Signage</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">
              Project Specifications
            </FormLabel>
            <FormControl>
              <Textarea {...field} placeholder="Describe your requirements, quantities, or attach file references." rows={5} className="px-0 py-3 bg-transparent border-0 border-b border-border font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0 resize-none" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="attachment" render={({
      field
    }) => <FormItem className="gap-3">
            <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground">
              Attachment
            </FormLabel>
            <FormControl>
              <Input type="file" accept=".jpg,.jpeg,.pdf" name={field.name} ref={field.ref} onBlur={field.onBlur} onChange={e => field.onChange(e.target.files?.[0])} className="h-12 px-0 bg-transparent border-0 border-b border-border font-default text-lg text-foreground placeholder:text-muted-foreground/60 focus-visible:border-primary focus-visible:ring-0" />
            </FormControl>
            <p className="font-default text-xs uppercase tracking-widest text-muted-foreground">
              Accepted file types: .jpg or .pdf only.
            </p>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="lookbook_signup" render={({
      field
    }) => <FormItem className="flex flex-row items-center gap-3 space-y-0">
            <FormControl>
              <Checkbox checked={field.value} onCheckedChange={field.onChange} className="border-border data-[state=checked]:bg-primary data-[state=checked]:border-primary data-[state=checked]:text-primary-foreground size-5" />
            </FormControl>
            <FormLabel className="font-default text-xs uppercase tracking-widest text-muted-foreground cursor-pointer">
              Receive technical updates and compliance bulletins
            </FormLabel>
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive bg-destructive/10">
          <p className="text-destructive font-default">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="group w-full h-14 bg-accent text-accent-foreground hover:bg-accent/90 font-default text-sm uppercase tracking-widest shadow-lg">
        <span>{isSubmitting ? "Transmitting..." : "Submit Request"}</span>
        <ArrowRight className="ml-2 size-4 transition-transform duration-300 group-hover:translate-x-1.5" />
      </Button>

      <div className="flex justify-end">
        <p className="font-default text-xs uppercase tracking-widest text-muted-foreground">
          Standard response time: 24 business hours
        </p>
      </div>
    </Form>;
}
export default function DirectEngineeringSupport() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const infoBlocks = [];
  return <section data-section-id="3822" id="direct-engineering-support" className="bg-background text-foreground pt-32 md:pt-48 lg:pt-[200px] pb-24 px-6 md:px-12 lg:px-20 overflow-hidden">
      <div className="max-w-[1440px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div ref={leftRef} className={`lg:col-span-5 transition-all duration-700 ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="flex items-center gap-3 mb-8">
              <span className="inline-block size-2 bg-primary" />
              <span className="font-default text-xs uppercase tracking-widest text-muted-foreground">
                § Engineering Support — Direct Line
              </span>
            </div>

            <h2 className="font-default font-bold uppercase leading-[0.95] tracking-tight text-foreground mb-10" style={{
            fontSize: "clamp(3rem, 7vw, 7rem)"
          }}>
              Submit
              <br />
              your
              <br />
              specs<span className="text-primary">.</span>
            </h2>

            <p className="font-default text-lg md:text-xl leading-relaxed text-muted-foreground max-w-md mb-12">Send us your sketch, ideas, files, material requirements, or part lists. We review, engrave, and ship to your exact specifications.</p>

            <div className="space-y-0 border-t border-border">
              {infoBlocks.map((block, i) => <div key={i} data-index={i} className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-2 md:gap-6 py-6 border-b border-border">
                  <span className="font-default text-xs uppercase tracking-widest text-muted-foreground pt-1">
                    {block.label}
                  </span>
                  <span className="font-default text-lg text-foreground">
                    {block.value}
                  </span>
                </div>)}
            </div>
          </div>

          <div ref={rightRef} className={`lg:col-span-7 lg:col-start-7 transition-all duration-700 delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <FormProvider formKey="engineering_support_form_contact" formId="engineering_support_form_contact" sectionName="direct-engineering-support" defaultValues={{
            name: "",
            email: "",
            phone: "",
            company: "",
            inquiry_type: "",
            message: "",
            lookbook_signup: false,
            attachment: undefined
          }} validationRules={{
            name: {
              required: true,
              minLength: 2
            },
            email: {
              required: true,
              pattern: "email"
            },
            phone: {
              pattern: "phone"
            },
            inquiry_type: {
              required: true
            },
            message: {
              required: true,
              minLength: 10
            },
            attachment: {
              custom: (file: any) => {
                if (!file) return null;
                const name = file?.name || "";
                if (!/\.(jpe?g|pdf)$/i.test(name)) {
                  return "Only .jpg or .pdf files are accepted";
                }
                return null;
              }
            }
          }} submitText="Submit Request" successMessage="Request received. Our engineering team will review your specifications shortly." errorMessage="Transmission error. Please verify your connection or contact us via email." className="relative bg-card/30 backdrop-blur-xl border border-border p-8 md:p-12 lg:p-16 shadow-4xl">
              <InquiryForm />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}